import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import AudioService from '../services/audioService';
import SonioxTranscriptionService from '../services/sonioxTranscriptionService';

// ═══════════════════════════════════════════════════════════════
// useDictationSession
// ═══════════════════════════════════════════════════════════════
//
// Lightweight hook that composes AudioService + SonioxTranscriptionService
// for short post-visit dictation turns in the note-preview screen. This is a
// stripped-down dictation flow — it doesn't need speaker
// diarization, transcript persistence, pause/resume, or reconnect-on-stall,
// because a dictation turn is typically a handful of seconds by a single
// speaker, and the UI owns lifecycle (start, stop, apply, retry).
//
// Phases (mirrors the FAB state machine):
//   idle       — no session running.
//   connecting — mic permission / WebSocket handshake in progress. Bounded
//                by CONNECT_TIMEOUT_MS: a permission prompt left pending, a
//                mic held by another app, or a hung token fetch lands in
//                `error` — never an endless spinner.
//   recording  — Soniox connected; mic is streaming and transcript is growing.
//   stopping   — user pressed stop; we're flushing finals and closing streams.
//   applying   — dictation stopped; caller is now sending to the backend.
//   error      — something failed; the hook is reset and ready to retry.
//
// Options:
//   onAutoStop(finalTranscript) — called when the safety cap stops a
//     recording. The consumer must run its usual apply path with the
//     transcript; without a handler the hook falls back to `error` so the UI
//     is never stranded in `applying` with no request in flight.
//   maxDurationMs — overrides the default 2-minute safety cap. Surfaces where
//     a longer take is legitimate (e.g. dictating a full telephone-encounter
//     message with lab results) pass a higher cap.
//
// Returned API:
//   { phase, isActive, interimTranscript, finalTranscript, fullTranscript,
//     elapsedMs, start, stop, reset, error, waveformSource }
//
// `waveformSource` is a React ref whose `.current` satisfies the shape
// expected by `./components/common/AudioWaveform.js`
// (exposes `getAudioFrequencyData(bars)`), so callers can pass it directly:
//   <AudioWaveform sessionManagerRef={waveformSource} isRecording={...} />

const DICTATION_PHASES = {
  IDLE: 'idle',
  CONNECTING: 'connecting',
  RECORDING: 'recording',
  STOPPING: 'stopping',
  APPLYING: 'applying',
  ERROR: 'error',
};

// Safety cap so a stuck "recording" state can't silently tie up the mic
// forever if the UI forgets to stop. Two minutes is well past any realistic
// dictation use-case (physicians dictate 5–30s at a time).
const MAX_DICTATION_MS = 2 * 60 * 1000;

// Watchdog on the connect leg. getUserMedia can sit unresolved forever (a
// permission prompt nobody answers, a mic held by another app) and nothing
// else bounds the `connecting` phase, so without this the UI spins forever.
// Both network legs inside connect() (token fetch, WS handshake) are bounded
// at 10s each, so 15s only trips when something is genuinely stuck.
const CONNECT_TIMEOUT_MS = 15_000;

export default function useDictationSession({
  onAutoStop,
  maxDurationMs = MAX_DICTATION_MS,
}: {
  onAutoStop?: (finalTranscript: string) => void;
  maxDurationMs?: number;
} = {}) {
  const [phase, setPhase] = useState(DICTATION_PHASES.IDLE);
  const [interimTranscript, setInterimTranscript] = useState('');
  const [finalTranscript, setFinalTranscript] = useState('');
  const [elapsedMs, setElapsedMs] = useState(0);
  const [error, setError] = useState<any>(null);

  // Services. Held in refs so a re-render never re-creates them.
  const audioServiceRef = useRef<any>(null);
  const transcriptionServiceRef = useRef<any>(null);

  // Waveform-compatible facade exposing getAudioFrequencyData() as the
  // extracted AudioWaveform component expects. We recompute frequency bars
  // on demand from audioService.analyzerNode.
  const waveformSource = useRef({
    getAudioFrequencyData(numBars = 24) {
      const audio = audioServiceRef.current;
      if (!audio?.analyzerNode || !audio.isRecording) {
        return Array.from({length: numBars}, () => 0);
      }
      const analyzer = audio.analyzerNode;
      const bufferLength = analyzer.frequencyBinCount;
      const dataArray = new Uint8Array(bufferLength);
      analyzer.getByteFrequencyData(dataArray);

      const sampleRate = audio.audioContext?.sampleRate || 16000;
      const startBin = Math.floor((300 * bufferLength) / (sampleRate / 2));
      const endBin = Math.min(
        Math.floor((3400 * bufferLength) / (sampleRate / 2)),
        bufferLength,
      );
      const range = endBin - startBin;
      if (range <= 0) return Array.from({length: numBars}, () => 0);

      const binSize = Math.max(1, Math.floor(range / numBars));
      const bars = [];
      for (let i = 0; i < numBars; i += 1) {
        let sum = 0;
        const start = startBin + i * binSize;
        const end = Math.min(start + binSize, endBin);
        for (let j = start; j < end; j += 1) sum += dataArray[j];
        bars.push(sum / (end - start) / 255);
      }
      return bars;
    },
  });

  // Ref-mirror of phase so async callbacks always read the latest value even
  // when they close over stale render-scoped state.
  const phaseRef = useRef(phase);
  useEffect(() => {
    phaseRef.current = phase;
  }, [phase]);

  // The transcript refs are the AUTHORITATIVE store; the useState values are
  // derived copies that exist only to render. Every mutation writes the ref
  // synchronously and then mirrors it into state. stop() must be able to
  // return the merged transcript in the same tick it computes it — mirroring
  // state into refs via effects lags a render behind, which used to make
  // stop() return a transcript missing the still-interim tail of the
  // dictation (short utterances got truncated to their first word).
  const finalTranscriptRef = useRef('');
  const interimTranscriptRef = useRef('');

  // Incremented whenever a session is started or torn down. In-flight async
  // continuations (the connect await in start(), socket callbacks) capture
  // the value at spawn time and bail if it has moved on, so a cancelled or
  // reset session can never resurrect the UI into `recording`.
  const sessionGenRef = useRef(0);

  // Kept in a ref so the auto-stop timer (armed up to the cap earlier)
  // always calls the consumer's latest handler.
  const onAutoStopRef = useRef(onAutoStop);
  useEffect(() => {
    onAutoStopRef.current = onAutoStop;
  }, [onAutoStop]);

  // Ref-mirror of the cap so start() (a stable callback) reads the latest
  // value without re-creating itself when the option changes.
  const maxDurationMsRef = useRef(maxDurationMs);
  useEffect(() => {
    maxDurationMsRef.current = maxDurationMs;
  }, [maxDurationMs]);

  const startedAtRef = useRef(0);
  const tickerRef = useRef<any>(null);
  const autoStopTimerRef = useRef<any>(null);

  // ───── Helpers ───────────────────────────────────────────────

  const clearTimers = useCallback(() => {
    if (tickerRef.current) {
      clearInterval(tickerRef.current);
      tickerRef.current = null;
    }
    if (autoStopTimerRef.current) {
      clearTimeout(autoStopTimerRef.current);
      autoStopTimerRef.current = null;
    }
  }, []);

  const teardownServices = useCallback(() => {
    // Invalidate any in-flight start()/callback continuations.
    sessionGenRef.current += 1;
    clearTimers();
    const transcription = transcriptionServiceRef.current;
    const audio = audioServiceRef.current;
    if (transcription) {
      try {
        transcription.disconnect();
      } catch (_) {
        /* noop */
      }
    }
    if (audio) {
      try {
        audio.stopCapture();
      } catch (_) {
        /* noop */
      }
    }
    transcriptionServiceRef.current = null;
    audioServiceRef.current = null;
  }, [clearTimers]);

  const reset = useCallback(() => {
    teardownServices();
    finalTranscriptRef.current = '';
    interimTranscriptRef.current = '';
    setPhase(DICTATION_PHASES.IDLE);
    setInterimTranscript('');
    setFinalTranscript('');
    setElapsedMs(0);
    setError(null);
  }, [teardownServices]);

  // ───── Start ─────────────────────────────────────────────────

  const start = useCallback(
    async (options: Record<string, any> = {}) => {
      if (
        phaseRef.current !== DICTATION_PHASES.IDLE &&
        phaseRef.current !== DICTATION_PHASES.ERROR
      ) {
        return;
      }

      const gen = ++sessionGenRef.current;

      finalTranscriptRef.current = '';
      interimTranscriptRef.current = '';
      setInterimTranscript('');
      setFinalTranscript('');
      setElapsedMs(0);
      setError(null);
      setPhase(DICTATION_PHASES.CONNECTING);

      const audio = new AudioService();
      const transcription = new SonioxTranscriptionService();
      audioServiceRef.current = audio;
      transcriptionServiceRef.current = transcription;

      const handleTranscript = (speakerData: any) => {
        if (!speakerData) return;

        // Sum finals across speakers. Dictation is always a single speaker,
        // but Soniox may still emit speaker indices — we ignore them.
        let newFinalText = '';
        let interim = '';
        if (Array.isArray(speakerData.speakers)) {
          speakerData.speakers.forEach((s: any) => {
            if (s.finalText) {
              newFinalText += (newFinalText ? ' ' : '') + s.finalText;
            }
            if (s.interimText) {
              interim += (interim ? ' ' : '') + s.interimText;
            }
          });
        }

        // Refs first (authoritative, read synchronously by stop()), then
        // state for rendering.
        if (newFinalText) {
          finalTranscriptRef.current = finalTranscriptRef.current
            ? `${finalTranscriptRef.current} ${newFinalText}`
                .replace(/\s+/g, ' ')
                .trim()
            : newFinalText.trim();
          setFinalTranscript(finalTranscriptRef.current);
        }
        interimTranscriptRef.current = interim;
        setInterimTranscript(interim);
      };

      const handleTranscriptError = (err: any) => {
        // A socket error that races a stop/teardown (gen already bumped)
        // must not yank the UI into `error` — the stop path owns it now.
        if (sessionGenRef.current !== gen) return;
        console.error('[useDictationSession] transcription error', err);
        setError(err?.message || 'Transcription service error');
        setPhase(DICTATION_PHASES.ERROR);
        teardownServices();
      };

      try {
        // Open the mic and the Soniox socket concurrently rather than in
        // series. getUserMedia (mic permission / device warm-up) and the
        // WebSocket handshake (Firebase token + connect + config) are the two
        // slow legs of starting a dictation, and they're independent — so
        // running them in parallel roughly halves perceived connect time.
        // This is safe because `sendAudio` buffers any PCM captured before the
        // socket is open and replays it in order the moment we connect, so no
        // audio is lost if the mic warms up first.
        const connectPromise = transcription.connect(
          handleTranscript,
          handleTranscriptError,
          {
            // English-only is the right default for US-market clinical
            // dictation; callers can pass `languageHints: ['en', 'es']` etc. if
            // they want multilingual dictation. We explicitly disable speaker
            // diarization — it's a single speaker and diarization just adds
            // tokens.
            languageHints: options.languageHints || ['en'],
            enableSpeakerDiarization: false,
          },
        );

        const capturePromise = audio.startCapture(
          // Send to THIS session's socket, not whatever the ref points at —
          // frames from a session being torn down must never leak into a
          // newer session's stream.
          pcm => transcription.sendAudio(pcm),
          {
            deviceId: options.deviceId,
          },
        );

        // Bound the whole connect leg. Losing legs keep running after the
        // watchdog fires, but teardown flags both services so a late
        // getUserMedia stops its tracks and a late token fetch aborts
        // instead of opening an orphaned socket.
        let watchdog: any = null;
        try {
          await Promise.race([
            Promise.all([connectPromise, capturePromise]),
            new Promise((_, rejectTimeout) => {
              watchdog = setTimeout(() => {
                rejectTimeout(
                  new Error(
                    'Could not connect the microphone. Check the microphone permission and try again.',
                  ),
                );
              }, CONNECT_TIMEOUT_MS);
            }),
          ]);
        } finally {
          clearTimeout(watchdog);
        }

        // Cancelled / reset / unmounted while we were connecting — the
        // session is already torn down; don't resurrect the recording UI.
        if (sessionGenRef.current !== gen) {
          try {
            transcription.disconnect();
          } catch (_) {
            /* noop */
          }
          try {
            audio.stopCapture();
          } catch (_) {
            /* noop */
          }
          return;
        }

        startedAtRef.current = Date.now();
        setPhase(DICTATION_PHASES.RECORDING);

        tickerRef.current = setInterval(() => {
          setElapsedMs(Date.now() - startedAtRef.current);
        }, 200);

        const capMs = maxDurationMsRef.current || MAX_DICTATION_MS;
        autoStopTimerRef.current = setTimeout(() => {
          if (phaseRef.current !== DICTATION_PHASES.RECORDING) return;
          // Hand the transcript to the consumer's apply path. stopInternal
          // parks the phase at `applying`, and only the consumer can drive
          // the backend request — without this callback the UI would show
          // an "Applying…" spinner forever with nothing in flight.
          // eslint-disable-next-line no-use-before-define
          stopInternal()
            .then(finalText => {
              const handler = onAutoStopRef.current;
              if (handler) {
                handler(finalText);
                return;
              }
              setError(
                `Dictation stopped automatically after ${Math.round(
                  capMs / 60_000,
                )} minutes.`,
              );
              setPhase(DICTATION_PHASES.ERROR);
            })
            .catch(() => {
              /* swallow — error path already set */
            });
        }, capMs);
      } catch (err) {
        if (sessionGenRef.current !== gen) {
          // Torn down while connecting (cancel/unmount) — nothing to
          // surface; just make sure this session's services are dead.
          try {
            transcription.disconnect();
          } catch (_) {
            /* noop */
          }
          try {
            audio.stopCapture();
          } catch (_) {
            /* noop */
          }
          return;
        }
        console.error('[useDictationSession] failed to start', err);
        setError(err?.message || 'Failed to start dictation');
        setPhase(DICTATION_PHASES.ERROR);
        teardownServices();
      }
      // `stopInternal` is defined below; useCallback with no deps is fine
      // because it only reads refs.
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [teardownServices],
  );

  // ───── Stop (produces the final transcript, tears down services) ────

  const stopInternal = useCallback(async () => {
    if (
      phaseRef.current !== DICTATION_PHASES.RECORDING &&
      phaseRef.current !== DICTATION_PHASES.CONNECTING
    ) {
      return finalTranscriptRef.current;
    }

    // Invalidate the in-flight start() continuation — we may still be in
    // `connecting` (cancel button), and a late-resolving connect must not
    // flip the UI back to `recording` after this stop.
    sessionGenRef.current += 1;

    setPhase(DICTATION_PHASES.STOPPING);
    clearTimers();

    const transcription = transcriptionServiceRef.current;
    const audio = audioServiceRef.current;

    // Stop pushing mic audio BEFORE finalizing so the finalize message is
    // the last thing in the wire buffer on our side.
    if (audio) {
      try {
        audio.stopCapture();
      } catch (_) {
        /* noop */
      }
    }

    // Ask Soniox to flush any non-final tokens, then wait a short window for
    // the finals to arrive on the transcript callback. 700ms is roughly the
    // Soniox endpoint latency; we cap at 1500ms to stay snappy.
    if (transcription) {
      try {
        transcription.finalize();
      } catch (_) {
        /* noop */
      }
    }

    // Always wait the full window. An empty interim buffer is NOT proof the
    // flush has landed — the last words spoken can still be in Soniox's
    // pipeline with no interim tokens emitted yet, so exiting early on
    // "interim is empty" would drop the tail of the dictation.
    await new Promise<void>(resolvePromise => {
      setTimeout(resolvePromise, 1500);
    });

    if (transcription) {
      try {
        transcription.disconnect();
      } catch (_) {
        /* noop */
      }
    }

    audioServiceRef.current = null;
    transcriptionServiceRef.current = null;

    // Fold any remaining interim into final (Soniox sometimes does not
    // finalize a very short utterance even on finalize(); accepting the
    // interim is better UX than throwing it away). Done imperatively on the
    // refs so the value we RETURN is the merged transcript — the previous
    // setState-updater version raced React's batching and could return a
    // transcript missing the interim tail, truncating short dictations.
    const remainingInterim = interimTranscriptRef.current.trim();
    if (remainingInterim) {
      finalTranscriptRef.current = finalTranscriptRef.current
        ? `${finalTranscriptRef.current} ${remainingInterim}`
            .replace(/\s+/g, ' ')
            .trim()
        : remainingInterim;
    }
    interimTranscriptRef.current = '';
    setFinalTranscript(finalTranscriptRef.current);
    setInterimTranscript('');

    setPhase(DICTATION_PHASES.APPLYING);
    return finalTranscriptRef.current;
  }, [clearTimers]);

  const stop = useCallback(async () => stopInternal(), [stopInternal]);

  // Called by the consumer when the backend apply succeeds or fails, to
  // return to idle. Separates UI lifecycle from audio lifecycle.
  const markApplied = useCallback(() => {
    finalTranscriptRef.current = '';
    interimTranscriptRef.current = '';
    setPhase(DICTATION_PHASES.IDLE);
    setInterimTranscript('');
    setFinalTranscript('');
    setElapsedMs(0);
  }, []);

  const markError = useCallback((err: any) => {
    setError(err?.message || String(err) || 'Unknown error');
    setPhase(DICTATION_PHASES.ERROR);
  }, []);

  // Tear down on unmount — callers should not need to worry about leaking
  // mic access if the page unmounts mid-dictation.
  useEffect(
    () => () => {
      teardownServices();
    },
    [teardownServices],
  );

  const fullTranscript = useMemo(() => {
    const parts = [finalTranscript, interimTranscript].filter(Boolean);
    return parts.join(' ').replace(/\s+/g, ' ').trim();
  }, [finalTranscript, interimTranscript]);

  const isActive =
    phase === DICTATION_PHASES.CONNECTING ||
    phase === DICTATION_PHASES.RECORDING ||
    phase === DICTATION_PHASES.STOPPING ||
    phase === DICTATION_PHASES.APPLYING;

  return {
    phase,
    isActive,
    isRecording: phase === DICTATION_PHASES.RECORDING,
    interimTranscript,
    finalTranscript,
    fullTranscript,
    elapsedMs,
    error,
    start,
    stop,
    reset,
    markApplied,
    markError,
    waveformSource,
  };
}

export const DICTATION_PHASE = DICTATION_PHASES;
